import React, { useState, useEffect, useRef } from 'react';
import { Users, User } from 'lucide-react';

interface Gusmester {
  id: number;
  name: string;
  bio: string;
  picture_url: string;
}

const Gusmasters: React.FC = () => {
  const [gusmesters, setGusmesters] = useState<Gusmester[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [truncatedIds, setTruncatedIds] = useState<Set<number>>(new Set());
  const bioRefs = useRef<Record<number, HTMLParagraphElement | null>>({});

  useEffect(() => {
    fetchGusmesters();
  }, []);

  useEffect(() => {
    const next = new Set<number>();
    gusmesters.forEach((gusmester) => {
      const el = bioRefs.current[gusmester.id];
      if (el && el.scrollHeight > el.clientHeight + 1) {
        next.add(gusmester.id);
      }
    });
    setTruncatedIds(next);
  }, [gusmesters]);

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const fetchGusmesters = async () => {
    try {
      const response = await fetch('/api/gusmesters');
      if (!response.ok) {
        throw new Error('Failed to fetch gusmesters');
      }
      const data = await response.json();
      setGusmesters(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
          <p className="mt-4 text-gray-600">Indlæser Gusmestre...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Fejl</div>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-blue-700 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Vores Gusmestre</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Mød de dedikerede Gusmestre, der skaber de fantastiske GUS saunaoplevelser. 
            Hver Gusmester bringer deres unikke personlighed og ekspertise med ind til deres gus.
          </p>
        </div>

        {/* Gusmesters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {gusmesters.map((gusmester) => (
            <div
              key={gusmester.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col"
            >
              {/* Image */}
              <div className="h-64 bg-gray-200 relative overflow-hidden flex-shrink-0">
                {gusmester.picture_url ? (
                  <img
                    src={gusmester.picture_url}
                    alt={gusmester.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      target.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`absolute inset-0 flex items-center justify-center ${gusmester.picture_url ? 'hidden' : ''}`}>
                  <User className="w-24 h-24 text-gray-400" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6 flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {gusmester.name}
                </h3>
                {gusmester.bio ? (
                  <>
                    <p
                      ref={(el) => { bioRefs.current[gusmester.id] = el; }}
                      className={`text-gray-600 leading-relaxed ${expandedIds.has(gusmester.id) ? '' : 'line-clamp-4'}`}
                    >
                      {gusmester.bio}
                    </p>
                    {truncatedIds.has(gusmester.id) && (
                      <button
                        onClick={() => toggleExpanded(gusmester.id)}
                        className="mt-1 text-sm font-medium text-blue-700 hover:text-blue-900"
                      >
                        {expandedIds.has(gusmester.id) ? 'Vis mindre' : 'Vis mere'}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 italic">
                    Ingen bio tilgængelig
                  </p>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-sky-50 border-t border-sky-100 flex-shrink-0">
                <div className="flex items-center text-sky-700">
                  <User className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">Certificeret Gusmester</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {gusmesters.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              Ingen Gusmestre fundet
            </h3>
            <p className="text-gray-600">
              Der er i øjeblikket ingen Gusmestre tilgængelige.
            </p>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Oplev GUS saunaen
            </h2>
            <p className="text-gray-600 mb-6">
              Book en saunagus med en af vores erfarne Gusmestre og oplev den utrolige gus oplevelse.
            </p>
            <a
              href="/sessions"
              className="inline-flex items-center px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white font-medium rounded-lg transition-colors duration-200"
            >
              Se tilgængelige sessioner
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Gusmasters; 